import { useState, type FormEvent } from "react";
import { ApiError } from "@plotlounge/shared/api";
import {
  waitlistSignupSchema,
  type WaitlistSource,
} from "@plotlounge/shared/validations";
import { getApiClient } from "@/lib/api-client";
import styles from "./WaitlistForm.module.scss";

interface WaitlistFormProps {
  source: WaitlistSource;
  emailLabel?: string;
  cityLabel?: string;
  submitLabel?: string;
  successMessage?: string;
  /** Pre-fill the city input. Used by /discover empty state. */
  defaultCity?: string;
}

type Status =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "success" }
  | { kind: "error"; message: string };

export function WaitlistForm({
  source,
  emailLabel = "email",
  cityLabel = "city (optional)",
  submitLabel = "Add me",
  successMessage = "You're on the list. We'll only email when we open in your city.",
  defaultCity = "",
}: WaitlistFormProps) {
  const [email, setEmail] = useState("");
  const [city, setCity] = useState(defaultCity);
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (status.kind === "submitting") return;

    const parsed = waitlistSignupSchema.safeParse({
      email,
      source,
      city: city.trim() === "" ? undefined : city,
    });
    if (!parsed.success) {
      setStatus({
        kind: "error",
        message:
          parsed.error.issues[0]?.message ??
          "Please check your email and try again.",
      });
      return;
    }

    setStatus({ kind: "submitting" });
    try {
      await getApiClient().post("/public/waitlist", parsed.data);
      setStatus({ kind: "success" });
      setEmail("");
      setCity("");
    } catch (err) {
      const message =
        err instanceof ApiError
          ? errorMessageFor(err)
          : "Something went wrong. Please try again.";
      setStatus({ kind: "error", message });
    }
  }

  if (status.kind === "success") {
    return (
      <p className={`${styles.status} ${styles.success}`} role="status">
        {successMessage}
      </p>
    );
  }

  return (
    <>
      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        <input
          name="email"
          type="email"
          required
          aria-label="Email"
          placeholder={emailLabel}
          className={styles.input}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
        />
        <input
          name="city"
          type="text"
          aria-label="City"
          placeholder={cityLabel}
          className={styles.input}
          value={city}
          onChange={(e) => setCity(e.target.value)}
          autoComplete="address-level2"
        />
        <button
          type="submit"
          className={styles.submit}
          disabled={status.kind === "submitting"}
        >
          {status.kind === "submitting" ? "…" : submitLabel}
        </button>
      </form>
      {status.kind === "error" && (
        <p className={`${styles.status} ${styles.error}`} role="alert">
          {status.message}
        </p>
      )}
    </>
  );
}

function errorMessageFor(err: ApiError): string {
  // Rate-limited submissions: surface the API's friendly message verbatim if
  // present. Anything else (5xx, network) falls back to a generic line.
  if (err.status === 429) {
    return (
      err.message || "Too many submissions from this address. Try again later."
    );
  }
  if (err.status >= 500) {
    return "Something went wrong on our end. Please try again.";
  }
  return err.message || "Please check your input and try again.";
}
